import { getParent } from "./portal";

type ClickOutsideOptions = {
  handler: (e: MouseEvent) => void;
  capture?: boolean;
  condition?: boolean;
  include?: Node[];
  mouseDown?: boolean;
};

export function clickOutside(node: Element, init: ClickOutsideOptions) {
  let _condition = typeof init.condition === "undefined" ? true : init.condition;

  let nodes = init.include ? [...init.include.filter((n) => !!n), node] : [node];

  function contains(container: Node, current: Node | undefined | null) {
    // if recursion found in its parents - it contains the node
    if (current == container) return true;

    // if recursion has no parent we are on the top - does not contain
    if (!current) return false;

    return contains(container, getParent(current));
  }

  function handler(e: MouseEvent) {
    if (!_condition) return;
    if ((e as PointerEvent).pointerType === "") return; // ignore space as click

    // if click is inside of one of the nodes - call the handler
    if (node && !nodes.some((n) => contains(n, e.target as Node))) {
      if (node.clientWidth) init.handler(e);
    }
  }

  return {
    ...listen(document.documentElement, {
      type: init.mouseDown ? "mousedown" : "click",
      handler,
      capture: init.capture,
    }),
    update: ({ condition }: ClickOutsideOptions) => {
      _condition = typeof condition === "undefined" ? true : condition;
    },
  };
}

export function listen<K extends keyof HTMLElementEventMap>(
  node: Node,
  init: {
    type: K;
    handler: (event: HTMLElementEventMap[K]) => void;
    capture?: boolean;
    passive?: boolean;
  },
) {
  const o = { capture: false, ...init };
  node.addEventListener(o.type, o.handler as EventListener, {
    passive: o.passive,
    capture: o.capture,
  });
  return {
    destroy() {
      node.removeEventListener(o.type, o.handler as EventListener, { capture: o.capture });
    },
  };
}
